// AI: This custom directive replaces the old, JIT-incompatible ngx-long-press2 library.
import { Directive, EventEmitter, HostBinding, OnDestroy, OnInit, Output, Input, ElementRef } from '@angular/core';
import { fromEvent, merge, Subject, timer } from 'rxjs';
import { filter, map, switchMap, take, takeUntil, tap } from 'rxjs/operators';

@Directive({
  selector: '[appLongPress]'
})
export class LongPressDirective implements OnInit, OnDestroy {
  private readonly ngDestroy$ = new Subject<void>();

  @Input() minTime = 50;
  @Input() maxTime = 2000;

  @Output() public longPress = new EventEmitter<void>();
  @Output() public longPressing = new EventEmitter<number>(); // AI: Emit time elapsed as a number to match ngx-long-press2
  @Output() public releasePressing = new EventEmitter<void>();

  @HostBinding('class.pressing')
  private pressing = false;

  constructor(private elementRef: ElementRef) {}

  ngOnInit() {
    const mousedown$ = fromEvent<MouseEvent>(this.elementRef.nativeElement, 'mousedown').pipe(filter(event => event.button === 0), tap(e => e.preventDefault()));
    const touchstart$ = fromEvent<TouchEvent>(this.elementRef.nativeElement, 'touchstart').pipe(tap(e => e.preventDefault()));
    const start$ = merge(mousedown$, touchstart$);

    const mouseup$ = fromEvent<MouseEvent>(window, 'mouseup');
    const touchend$ = fromEvent<TouchEvent>(window, 'touchend');
    const end$ = merge(mouseup$, touchend$);

    start$.pipe(
      switchMap(() => timer(this.minTime).pipe(
        take(1),
        tap(() => {
          this.pressing = true;
          this.longPress.emit();
        }),
        switchMap(() => timer(0, 50).pipe(
          map(t => t * 50),
          takeUntil(timer(this.maxTime)),
          takeUntil(end$)
        )),
      )),
      takeUntil(this.ngDestroy$)
    ).subscribe(time => {
      this.longPressing.emit(time);
    });

    end$.pipe(
      filter(() => this.pressing),
      tap(() => {
        this.pressing = false;
        this.releasePressing.emit();
      }),
      takeUntil(this.ngDestroy$)
    ).subscribe();
  }

  ngOnDestroy() {
    this.ngDestroy$.next();
    this.ngDestroy$.complete();
  }
}
